import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import {
  PrismAccordion,
  PrismBadge,
  PrismIcon,
  PrismTable,
} from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { t } from 'ember-intl';
import { gt } from 'ember-truth-helpers';
import { OVERRIDE_TYPES } from 'lumen/utils/constants/pricing-assistant-constants';
import { getOverrideVariant } from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';

import TdAutomationCheckbox from './cells/td-automation-checkbox';
import TdMinLosRecommendation from './cells/td-min-los-recommendation';
import TdRateGroupName from './cells/td-rate-group-name';
import styles from './rate-groups-accordion.module.css';
import type { StayRestrictionsAccordionState } from './stay-restrictions-accordion';

/* The slice of a MinLOS state the row's cells read; MinLosRestrictionState
   satisfies it structurally. */
export interface RateGroupRowState extends Pick<
  StayRestrictionsAccordionState,
  | 'automationOn'
  | 'errorMessage'
  | 'formattedRecommendation'
  | 'isLoading'
  | 'onBlur'
  | 'onTypeChange'
  | 'restrictionType'
  | 'toggleAutomation'
  | 'touched'
  | 'updateValue'
  | 'value'
  | 'warningMessage'
> {
  formattedCurrentValue: string;
}

export interface RateGroupRow extends CustomEmberTableRow {
  /* Snapshotted when the modal opens so a row keeps its section while edited. */
  isRestricted: boolean;
  /* Declared on both row types so the cells can narrow on it. */
  isSection?: false;
  name: string;
  /* Namespaces the row's radio group name and the element ids its controls are
     labelled by. */
  rateGroupId: string;
  /* The row's MinLOS state; the cells read their values and handlers from it. */
  state: RateGroupRowState;
}

/* A collapsible section grouping restricted or not restricted rate groups. */
export interface RateGroupSectionRow extends CustomEmberTableRow {
  children: RateGroupRow[];
  isCollapsed: boolean;
  isSection: true;
  name: string;
}

interface RateGroupsAccordionSignature {
  Args: {
    overrideCount: number;
    rows: RateGroupRow[];
    showEmptyState?: boolean;
  };
  Blocks: {
    emptyState: [];
    settingsLink: [];
  };
}

export default class RateGroupsAccordion extends Component<RateGroupsAccordionSignature> {
  @service declare intl: Services['intl'];

  get columns(): CustomEmberTableColumn[] {
    return [
      {
        isSortable: false,
        textAlign: 'left',
        name: this.intl.t('pricing_assistant.stay_restrictions.rate_group'),
        tdClassNames: styles['top-aligned-cell'],
        tdComponent: TdRateGroupName,
        valuePath: 'name',
        width: 136,
      },
      {
        isSortable: false,
        textAlign: 'left',
        name: this.intl.t('pricing_manager.automation_on'),
        tdClassNames: styles['top-aligned-cell'],
        tdComponent: TdAutomationCheckbox,
        valuePath: 'state.automationOn',
        width: 103,
      },
      {
        isSortable: false,
        textAlign: 'left',
        name: this.intl.t('pricing_manager.restrictions.min_los_label'),
        tdClassNames: 'p-200',
        tdComponent: TdMinLosRecommendation,
        valuePath: 'state.formattedRecommendation',
        minWidth: 212,
        width: 212,
      },
      {
        isSortable: false,
        textAlign: 'left',
        name: this.intl.t(
          'pricing_assistant.stay_restrictions.min_los_currently_in_pms',
        ),
        tdClassNames: styles['top-aligned-cell'],
        thSortWrapperClassNames: 'line-clamp-2',
        valuePath: 'state.formattedCurrentValue',
        width: 127,
      },
    ];
  }

  /* A single error line above the table; every erroring input keeps its own
     border. A missing value wins over a too-low one, matching the missing-first
     order of DYNAMIC_RATE_DISCOUNT_RANGE_ERROR_KIND_PRECEDENCE. Otherwise the
     first row in error decides. */
  get errorMessage(): string | null {
    const rowsWithError = this.args.rows.filter(
      (row) => row.state.errorMessage,
    );

    const missingValueRow = rowsWithError.find(
      (row) => row.state.value === null,
    );

    return (missingValueRow ?? rowsWithError[0])?.state.errorMessage ?? null;
  }

  /* Cached because ember-table writes isCollapsed back onto the section rows, and a
     rebuild would reset it and drop the MinLOS input's focus. */
  @cached
  get tableRows(): RateGroupSectionRow[] {
    const restrictedRows = this.args.rows.filter((row) => row.isRestricted);
    const notRestrictedRows = this.args.rows.filter((row) => !row.isRestricted);

    return [
      {
        children: restrictedRows,
        isCollapsed: false,
        isSection: true,
        name: this.intl.t(
          'pricing_assistant.stay_restrictions.restricted_count',
          { count: restrictedRows.length },
        ),
      },
      {
        children: notRestrictedRows,
        isCollapsed: true,
        isSection: true,
        name: this.intl.t(
          'pricing_assistant.stay_restrictions.not_restricted_count',
          { count: notRestrictedRows.length },
        ),
      },
    ];
  }

  get warningMessage(): string | null {
    const rowWithWarning = this.args.rows.find(
      (row) => row.state.warningMessage,
    );

    return rowWithWarning?.state.warningMessage ?? null;
  }

  <template>
    <PrismAccordion
      @initiallyOpened={{true}}
      @title={{t "pricing_assistant.stay_restrictions.rate_groups"}}
      class="mx-500 my-400"
      data-test-id="rate-groups-accordion"
    >
      <:summaryAfter>
        {{#if (gt @overrideCount 0)}}
          <PrismBadge
            @iconName={{getOverrideVariant OVERRIDE_TYPES.MIN_LOS}}
            @label={{t
              "pricing_assistant.stay_restrictions.override_count"
              count=@overrideCount
            }}
            @type="warning"
            data-test-id="rate-groups-override-badge"
          />
        {{/if}}
      </:summaryAfter>
      <:default>
        {{#if @showEmptyState}}
          {{yield to="emptyState"}}
        {{else}}
          {{#if this.errorMessage}}
            <div
              class="text-critical-default mb-400 flex items-start gap-100"
              data-test-id="rate-groups-error"
            >
              <PrismIcon @color="critical" @name="circle-minus" @size="small" />
              <p class="text-200--medium">{{this.errorMessage}}</p>
            </div>
          {{/if}}
          {{#if this.warningMessage}}
            <div
              class="text-warning-default mb-400 flex items-start gap-100"
              data-test-id="rate-groups-warning"
            >
              <PrismIcon @color="warning" @name="warning" @size="small" />
              <p class="text-200--medium">{{this.warningMessage}}</p>
            </div>
          {{/if}}
          <PrismTable
            @columns={{this.columns}}
            @fillMode="equal-column"
            @highlightTitleColumn={{false}}
            @rows={{this.tableRows}}
            @widthConstraint="eq-container"
            class={{styles.table}}
            data-test-id="rate-groups-table"
          />
          {{#if (has-block "settingsLink")}}
            <div class="mt-400">
              {{yield to="settingsLink"}}
            </div>
          {{/if}}
        {{/if}}
      </:default>
    </PrismAccordion>
  </template>
}
